import { LitElement, html, css, unsafeCSS } from 'lit';
import { getMessage, getMessagePlural } from '@/localization.js';
import messages from './localization.json';
import styles from './catalog-basket-drag.scss?inline';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import { USER_SELECT_NONE_CLASS } from '@/components/catalog/constants.js';

export default class SotbitCatalogBasketDrag extends LitElement {
    static styles = [
        css`
            ${unsafeCSS(styles)}
        `,
    ];

    static properties = {
        quantity: { type: String },
        price: { type: String },
        priceFormatted: { type: String, attribute: 'price-formatted' },
        minOrderPriceParams: { type: String, attribute: 'min-order-price-params' },
        basketLink: { type: String, attribute: 'basket-link' },
        key: { type: String },
        isDragging: { type: Boolean, state: true },
        _setUserSelectNone: { type: Boolean, state: true },
        useMinOrderPrice: { type: Boolean, attribute: 'use-min-order-price' },
        open: { type: Boolean, state: true },
    };

    constructor() {
        super();

        this.quantity = '0';
        this.price = '';
        this.priceFormatted = '';
        this.basketLink = '';
        this.isDragging = false;
        this.offsetStore = { x: 0, y: 0, windowInnerWidth: 0, windowInnerHeight: 0 };
        this.offset = { x: 0, y: 0, windowInnerWidth: 0, windowInnerHeight: 0 };
        this.key = 'basketDragAndDrop';
        this._setUserSelectNone = false;

        this._handleMouseMoveBind = throttle(this._handleMouseMove.bind(this), 20);
        this._handleMouseUpBind = this._handleMouseUp.bind(this);
        this._handleTouchMoveBind = throttle(this._handleTouchMove.bind(this), 20);
        this._handleTouchEndBind = this._handleTouchEnd.bind(this);
        this._handleResizeBind = debounce(this._handleResize.bind(this), 100);

        this.open = false;
        this.useMinOrderPrice = false;
        this.wasDragged = false;
    }

    connectedCallback() {
        super.connectedCallback();

        const storageData = JSON.parse(localStorage.getItem(this.key));

        if (storageData) {
            Object.assign(this.offset, storageData);
            Object.assign(this.offsetStore, storageData);
            this.initPosition();
        }

        window.addEventListener('resize', this._handleResizeBind);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        window.removeEventListener('resize', this._handleResizeBind);
    }

    attributeChangedCallback(name, oldValue, newValue) {
        super.attributeChangedCallback(name, oldValue, newValue);

        if (name === 'min-order-price-params') {
            try {
                this.minOrderPriceParams = JSON.parse(newValue);
            } catch {
                this.minOrderPriceParams = {};
            }
        }
    }

    initPosition() {
        const windowInnerWidth = window.innerWidth;
        const windowInnerHeight = window.innerHeight;

        const xRatio = this.offset.x / this.offset.windowInnerWidth;
        const x = windowInnerWidth * xRatio;
        const yRatio = this.offset.y / this.offset.windowInnerHeight;
        const y = windowInnerHeight * yRatio;

        this._moveTo(x, y);
    }

    _startDrag(x, y) {
        if (this.isDragging) {
            return;
        }

        this.isDragging = true;
        this.rect = this.getBoundingClientRect();
        this.offset.x = Math.max(x, this.rect.left) - Math.min(x, this.rect.left);
        this.offset.y = Math.max(y, this.rect.top) - Math.min(y, this.rect.top);

        if (!document?.body?.classList?.contains(USER_SELECT_NONE_CLASS)) {
            document?.body?.classList?.add(USER_SELECT_NONE_CLASS);
            this._setUserSelectNone = true;
            if (window?.getSelection) {
                window?.getSelection()?.removeAllRanges();
            } else if (document?.selection) {
                document?.selection?.empty();
            }
        }
    }

    _moveDrag(x, y) {
        if (!this.isDragging) {
            return;
        }

        this.wasDragged = true;

        const maxX = document.body.clientWidth - (this.rect?.width || 0);
        const maxY = window.innerHeight - (this.rect?.height || 0);

        x = Math.max(0, Math.min(x - this.offset.x, maxX));
        y = Math.max(0, Math.min(y - this.offset.y, maxY));

        this._moveTo(x, y);
    }

    _moveTo(x, y) {
        this.offsetStore.x = x;
        this.offsetStore.y = y;

        this.style.left = `${this.offsetStore.x}px`;
        this.style.top = `${this.offsetStore.y}px`;
        this.style.bottom = 'auto';

        if (!this.hasAttribute('moved')) {
            this.setAttribute('moved', '');
        }
    }

    _endDrag() {
        if (!this.isDragging) {
            return;
        }

        if (this._setUserSelectNone) {
            document?.body?.classList?.remove(USER_SELECT_NONE_CLASS);
            this._setUserSelectNone = false;
        }

        this.isDragging = false;

        this.offsetStore.windowInnerWidth = window.innerWidth;
        this.offsetStore.windowInnerHeight = window.innerHeight;

        Object.assign(this.offset, this.offsetStore);

        localStorage.setItem(this.key, JSON.stringify(this.offsetStore));
    }

    _fixPosition() {
        const rect = this.getBoundingClientRect();

        this._startDrag(rect.right, rect.top);
        this._moveDrag(rect.right, rect.top);
        this._endDrag();
    }

    _handleMouseDown(e) {
        this._startDrag(e.clientX, e.clientY);

        document.addEventListener('mousemove', this._handleMouseMoveBind);
        document.addEventListener('mouseup', this._handleMouseUpBind);
    }

    _handleMouseMove(e) {
        this._moveDrag(e.clientX, e.clientY);
    }

    _handleMouseUp() {
        this._endDrag();

        document.removeEventListener('mousemove', this._handleMouseMoveBind);
        document.removeEventListener('mouseup', this._handleMouseUpBind);

        localStorage.setItem(this.key, JSON.stringify(this.offsetStore));
    }

    _handleTouchStart(e) {
        const touch = e.touches[0];
        this._startDrag(touch.clientX, touch.clientY);

        document.addEventListener('touchmove', this._handleTouchMoveBind, {
            passive: false,
        });
        document.addEventListener('touchend', this._handleTouchEndBind);
    }

    _handleTouchMove(e) {
        e.preventDefault();
        const touch = e.touches[0];
        this._moveDrag(touch.clientX, touch.clientY);
    }

    _handleTouchEnd() {
        document.removeEventListener('touchmove', this._handleTouchMoveBind);
        document.removeEventListener('touchend', this._handleTouchEndBind);

        this._endDrag();
    }

    _handleResize() {
        if (!this.hasAttribute('moved')) {
            return;
        }

        this.initPosition();
    }

    _handleSmallStateClick(event) {
        if (this.minOrderPriceParams.useMinOrderPrice !== true) {
            return;
        }

        event.preventDefault();
        this.openFullState();
    }

    openFullState() {
        this.initCoords = this.getBoundingClientRect();
        this.open = true;
        this.wasDragged = false;

        requestAnimationFrame(() => {
            const coord = this._getFullCoordinates(this.initCoords);

            for (const key in coord) {
                if (key === 'animationDirection') {
                    continue;
                }

                this.style[key] = typeof coord[key] === 'number' ? `${coord[key]}px` : coord[key];
            }

            this.style.width = `${this.initCoords.width}px`;
            this.style.height = `${this.initCoords.height}px`;

            requestAnimationFrame(() => {
                this.style.width = `${coord.width}px`;
                this.style.height = `${coord.height}px`;
            });
        });
    }

    closeFullState() {
        requestAnimationFrame(() => {
            this.style.width = `${this.initCoords.width}px`;
            this.style.height = `${this.initCoords.height}px`;

            const currentCoords = this.getBoundingClientRect();

            this.addEventListener(
                'transitionend',
                () => {
                    this.open = false;

                    this.style.width = `auto`;
                    this.style.height = `auto`;

                    let coord = {};

                    if (this.wasDragged) {
                        coord = { left: currentCoords.left, top: currentCoords.top, bottom: 'auto', right: 'auto' };
                    } else {
                        coord = { left: this.initCoords.left, top: this.initCoords.top, bottom: 'auto', right: 'auto' };
                    }

                    for (const key in coord) {
                        this.style[key] = typeof coord[key] === 'number' ? `${coord[key]}px` : coord[key];
                    }

                    this.wasDragged = false;
                },
                { once: true }
            );
        });
    }

    _getFullCoordinates(initCoords) {
        const relativeElementOriginalRect = this.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const windowWidth = document.body.clientWidth;
        const spaceBelow = viewportHeight - initCoords.top;
        const spaceAbove = relativeElementOriginalRect.top;
        const spaceLeft = relativeElementOriginalRect.left;

        const result = {
            width: relativeElementOriginalRect.width,
            height: relativeElementOriginalRect.height,
        };

        const setTopPosition = () => {
            result.top = 'auto';
            result.bottom = viewportHeight - initCoords.bottom;
        };

        const setBottomPosition = () => {
            result.bottom = 'auto';
            result.top = initCoords.top;
        };

        const setLeftPosition = () => {
            result.left = 'auto';
            result.right = windowWidth - initCoords.right;
        };

        const setRightPosition = () => {
            result.right = 'auto';
            result.left = initCoords.left;
        };

        const contentHeight = this?.offsetHeight || this?.scrollHeight;
        const contentWidth = this?.offsetWidth || this?.scrollWidth;
        const fitsBelow = spaceBelow >= contentHeight;

        if (!fitsBelow && spaceAbove > spaceBelow) {
            setTopPosition();
        } else {
            setBottomPosition();
        }

        if (windowWidth - contentWidth < spaceLeft) {
            setLeftPosition();
        } else {
            setRightPosition();
        }

        return result;
    }

    render() {
        return html` <div class="basket-wrapper ${this.open ? ' open' : ''}">
            <div class="basket${this.open ? ' open' : ''}">
                <sotbit-button-icon
                    class="basket-drag${this.isDragging ? ' active' : ''}"
                    @mousedown="${this._handleMouseDown}"
                    @touchstart="${this._handleTouchStart}"
                    theme="${!this.open ? 'dark' : ''}"
                    variant="secondary"
                    background
                >
                    <sotbit-icon slot="content" size="s" icon="drag-indicator" no-padding></sotbit-icon>
                </sotbit-button-icon>
                <a
                    class="basket-info"
                    href="${this.basketLink}"
                    ?disabled="${this.isDragging}"
                    @click="${this._handleSmallStateClick}"
                    style="${this.open ? 'visibility:hidden; position: absolute;' : ''}"
                >
                    <span class="basket-info-icon">
                        <sotbit-button-icon theme="dark" variant="secondary" badge badge-numeric="${this.quantity}">
                            <sotbit-icon slot="content" size="l" icon="shopping-cart" no-padding></sotbit-icon>
                        </sotbit-button-icon>
                    </span>
                    <span>${this.priceFormatted}</span>
                </a>
                <div class="basket-open-info" style="${!this.open ? 'visibility:hidden; position: absolute;' : ''}">
                    <div class="basket-open-info-title">
                        <span>
                            ${getMessage(messages, 'countTitle')}
                            <span class="basket-open-info-count"
                                >${getMessagePlural(messages, 'itemsCount', Number(this.quantity), {
                                    '#COUNT#': this.quantity,
                                })}</span
                            >
                        </span>
                        <sotbit-icon
                            size="m"
                            icon="close"
                            variant="secondary"
                            pointer=""
                            @click="${this.closeFullState}"
                        ></sotbit-icon>
                    </div>

                    <sotbit-progress-bar
                        class="basket-open-info-min-price-progress"
                        current="${Object.hasOwn(this.minOrderPriceParams, 'currentSum')
                            ? this.minOrderPriceParams.currentSum
                            : 0}"
                        limit="${Object.hasOwn(this.minOrderPriceParams, 'minPrice')
                            ? this.minOrderPriceParams.minPrice
                            : 0}"
                        show-value=""
                        label-title="${getMessage(messages, 'minOrderPriceTitle')}"
                        label-value="${Object.hasOwn(this.minOrderPriceParams, 'minPriceFormatted')
                            ? this.minOrderPriceParams.minPriceFormatted
                            : ''}"
                    ></sotbit-progress-bar>

                    <div class="basket-open-info-total">
                        <span>
                            ${getMessage(messages, 'sumTitle')}
                            <span class="basket-open-info-total-value">${this.priceFormatted}</span>
                        </span>
                        <sotbit-button variant="primary" background="" is-link="" href="${this.basketLink}">
                            <span slot="content">${getMessage(messages, 'basketBtn')}</span>
                        </sotbit-button>
                    </div>
                </div>
            </div>
        </div>`;
    }
}
